import { db } from '../helpers/firebase';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where
} from 'firebase/firestore';

/**
 * Script to fix Content Extra data issues:
 * 1. Remove "deadline-exceeded" errors from chapters
 * 2. Fix Russian and German lecture data structure (merge split documents)
 */

const fixContentExtraData = async () => {
  console.log('🚀 Starting Content Extra data fix...');

  try {
    // Step 1: Fix deadline-exceeded errors in chapters
    await fixDeadlineExceededErrors();

    // Step 2: Fix Russian and German lecture structure
    await fixRussianAndGermanLectures();

    console.log('✅ Content Extra data fix completed successfully!');

  } catch (error) {
    console.error('❌ Error fixing Content Extra data:', error);
    throw error;
  }
};

/**
 * Remove deadline-exceeded errors from all chapters
 */
const fixDeadlineExceededErrors = async () => {
  console.log('\n📚 Step 1: Fixing deadline-exceeded errors in chapters...');

  const languages = ['en', 'es', 'ru', 'de', 'fr', 'it', 'pt']; // Add all languages you have
  const sections = ['lectures', 'books'];

  for (const language of languages) {
    for (const section of sections) {
      console.log(`\n🔍 Checking ${section} in ${language}...`);

      try {
        // Get all content items in this section
        const contentCollection = collection(db, 'contentSection', language, section);
        const contentSnapshot = await getDocs(contentCollection);

        if (contentSnapshot.empty) {
          console.log(`   ℹ️  No content found in ${section}/${language}`);
          continue;
        }

        console.log(`   📄 Found ${contentSnapshot.size} content items`);

        for (const contentDoc of contentSnapshot.docs) {
          const contentId = contentDoc.id;
          console.log(`   📚 Processing content: ${contentId}`);

          // Get chapters from subcollection
          const chaptersCollection = collection(db, 'contentSection', language, section, contentId, 'chapters');
          const chaptersSnapshot = await getDocs(chaptersCollection);

          if (chaptersSnapshot.empty) {
            console.log(`      ⚠️  No chapters found for ${contentId}`);
            continue;
          }

          console.log(`      📖 Found ${chaptersSnapshot.size} chapters`);

          for (const chapterDoc of chaptersSnapshot.docs) {
            const chapterId = chapterDoc.id;
            const chapterData = chapterDoc.data();

            let needsUpdate = false;
            let updateData = {};

            // Fix deadline-exceeded errors
            if (chapterData.audioError && chapterData.audioErrorMessage === 'deadline-exceeded') {
              console.log(`      🔧 Fixing deadline-exceeded error for chapter ${chapterId}`);
              updateData.audioError = false;
              updateData.audioErrorMessage = null;
              needsUpdate = true;
            }

            // Set accessLevel if missing (FREEMIUM MODEL)
            if (!chapterData.accessLevel) {
              const accessLevel = parseInt(chapterId) === 1 ? 'FREE' : 'PRO';
              console.log(`      🎯 Setting access level for chapter ${chapterId}: ${accessLevel}`);
              updateData.accessLevel = accessLevel;
              needsUpdate = true;
            }

            if (needsUpdate) {
              const chapterRef = doc(db, 'contentSection', language, section, contentId, 'chapters', chapterId);
              await updateDoc(chapterRef, {
                ...updateData,
                fixedAt: new Date().toISOString()
              });
              console.log(`      ✅ Updated chapter ${chapterId}`);
            }
          }
        }

        console.log(`   ✅ Completed fixing ${section} in ${language}`);

      } catch (error) {
        console.error(`   ❌ Error processing ${section}/${language}:`, error);
      }
    }
  }

  console.log('\n✅ Step 1 completed: Fixed deadline-exceeded errors and set access levels');
};

/**
 * Fix Russian and German lecture structure
 */
const fixRussianAndGermanLectures = async () => {
  console.log('\n📚 Step 2: Fixing Russian and German lecture structure...');

  const languagesToFix = ['ru', 'de'];
  const section = 'lectures';

  for (const language of languagesToFix) {
    console.log(`\n🔍 Fixing ${language} lectures...`);

    try {
      // Get all lectures in this language
      const lecturesCollection = collection(db, 'contentSection', language, section);
      const lecturesSnapshot = await getDocs(lecturesCollection);

      if (lecturesSnapshot.empty) {
        console.log(`   ℹ️  No lectures found in ${language}`);
        continue;
      }

      console.log(`   📄 Found ${lecturesSnapshot.size} lecture documents`);

      // Identify the documents
      let documentWithId1 = null;
      let documentWithRandomId = null;

      for (const lectureDoc of lecturesSnapshot.docs) {
        const lectureId = lectureDoc.id;
        const lectureData = lectureDoc.data();

        console.log(`   📄 Document ID: ${lectureId}, Title: ${lectureData.title || 'No title'}`);

        if (lectureId === '1') {
          documentWithId1 = { id: lectureId, data: lectureData };
          console.log(`      ℹ️  Found document with ID "1" (may be empty)`);
        } else {
          // Check if this document has the main lecture data (title, author, etc.)
          if (lectureData.title && lectureData.author) {
            documentWithRandomId = { id: lectureId, data: lectureData };
            console.log(`      ℹ️  Found document with random ID "${lectureId}" (has main data)`);
          }
        }
      }

      // We need at least the random ID document to proceed
      if (!documentWithRandomId) {
        console.log(`   ⚠️  No random ID document found to fix`);
        continue;
      }

      console.log(`\n🔧 Copying data from "${documentWithRandomId.id}" to document "1"...`);

      // Check existing chapters in ID "1" subcollection first
      const chaptersCollection = collection(db, 'contentSection', language, section, '1', 'chapters');
      const chaptersSnapshot = await getDocs(chaptersCollection);
      console.log(`   📖 Document "1" currently has ${chaptersSnapshot.size} chapters in subcollection`);

      // Step 1: Copy all main data from random ID document to ID "1" document
      const lectureRef1 = doc(db, 'contentSection', language, section, '1');

      let dataToMerge = {
        // Copy all data from random ID document
        ...documentWithRandomId.data,
        // Preserve any existing data from ID "1" if it exists
        ...(documentWithId1 ? documentWithId1.data : {}),
        // Ensure essential fields from random ID take priority
        title: documentWithRandomId.data.title,
        author: documentWithRandomId.data.author,
        lang: documentWithRandomId.data.lang || language,
        voice: documentWithRandomId.data.voice,
        type: documentWithRandomId.data.type || section,
        // Ensure proper ID
        id: '1',
        // Add migration info
        fixedAt: new Date().toISOString(),
        fixedFrom: documentWithRandomId.id,
        originalRandomId: documentWithRandomId.id
      };

      // Add imageUrl only if it's not undefined
      if (documentWithRandomId.data.imageUrl !== undefined) {
        dataToMerge.imageUrl = documentWithRandomId.data.imageUrl;
      }

      // Add createdAt only if it's not undefined
      if (documentWithRandomId.data.createdAt !== undefined) {
        dataToMerge.createdAt = documentWithRandomId.data.createdAt;
      }

      // Remove any old chapter arrays - chapters should be in subcollection
      delete dataToMerge.chapters;

      // Clean undefined values from the data (Firestore doesn't accept undefined)
      dataToMerge = Object.fromEntries(
        Object.entries(dataToMerge).filter(([key, value]) => value !== undefined)
      );

      console.log(`   🧹 Cleaned data, fields to write:`, Object.keys(dataToMerge));

      await setDoc(lectureRef1, dataToMerge);
      console.log(`   ✅ Copied main data to document "1"`);
      console.log(`      - Title: ${dataToMerge.title}`);
      console.log(`      - Author: ${dataToMerge.author}`);
      console.log(`      - Voice: ${dataToMerge.voice}`);

      // Step 2: Verify chapters still exist (they should be preserved)
      const finalChaptersSnapshot = await getDocs(chaptersCollection);
      console.log(`   📖 Document "1" still has ${finalChaptersSnapshot.size} chapters after merge`);

      // Step 3: Delete the random ID document
      const randomDocRef = doc(db, 'contentSection', language, section, documentWithRandomId.id);
      await deleteDoc(randomDocRef);
      console.log(`   🗑️  Deleted document with random ID "${documentWithRandomId.id}"`);

      console.log(`✅ Fixed ${language} lectures structure successfully`);

    } catch (error) {
      console.error(`   ❌ Error fixing ${language} lectures:`, error);
    }
  }

  console.log('✅ Step 2 completed: Russian and German lecture structure fixed');
};

/**
 * Utility function to inspect current state (run this first to see what needs fixing)
 */
const inspectContentExtraData = async () => {
  console.log('🔍 Inspecting Content Extra data structure...');

  const languages = ['ru', 'de'];
  const section = 'lectures';

  for (const language of languages) {
    console.log(`\n📚 Inspecting ${language} lectures:`);

    try {
      const lecturesCollection = collection(db, 'contentSection', language, section);
      const lecturesSnapshot = await getDocs(lecturesCollection);

      console.log(`   Found ${lecturesSnapshot.size} documents:`);

      for (const lectureDoc of lecturesSnapshot.docs) {
        const lectureId = lectureDoc.id;
        const lectureData = lectureDoc.data();

        console.log(`\n   📄 Document ID: ${lectureId}`);
        console.log(`      Title: ${lectureData.title || 'No title'}`);
        console.log(`      Author: ${lectureData.author || 'No author'}`);
        console.log(`      Type: ${lectureData.type || 'No type'}`);
        console.log(`      Voice: ${lectureData.voice || 'No voice'}`);
        console.log(`      Has text array: ${Array.isArray(lectureData.text)}`);
        console.log(`      Has chapters array: ${Array.isArray(lectureData.chapters)}`);

        // Check chapters subcollection
        const chaptersCollection = collection(db, 'contentSection', language, section, lectureId, 'chapters');
        const chaptersSnapshot = await getDocs(chaptersCollection);
        console.log(`      Chapters in subcollection: ${chaptersSnapshot.size}`);

        // Check for chapters with deadline-exceeded errors
        let deadlineErrors = 0;
        for (const chapterDoc of chaptersSnapshot.docs) {
          const chapterData = chapterDoc.data();
          if (chapterData.audioError && chapterData.audioErrorMessage === 'deadline-exceeded') {
            deadlineErrors++;
          }
        }
        if (deadlineErrors > 0) {
          console.log(`      ⚠️  Chapters with deadline-exceeded errors: ${deadlineErrors}`);
        }
      }

    } catch (error) {
      console.error(`   ❌ Error inspecting ${language}:`, error);
    }
  }
};

// Export functions for use
export { fixContentExtraData, inspectContentExtraData };

// Auto-run the fix when this module is imported (comment out if you want to run manually)
console.log('📋 Content Extra fix script loaded. Call fixContentExtraData() to run the fix.');

// Uncomment to auto-run:
// fixContentExtraData().catch(console.error);